import React, { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { Text, Box, Octahedron, Plane } from '@react-three/drei'
import { Physics, useBox, usePlane } from '@react-three/cannon'

// ─── useSlerp ─────────────────────────────────────────────────────────────────
function useSlerp() {
  const ref = useRef()
  const mouse = useRef({ x: 0, y: 0 })
  const target = useRef(new THREE.Quaternion())

  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 0.3
      mouse.current.y = (e.clientY / window.innerHeight - 0.5) * 0.3
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  useFrame(() => {
    if (!ref.current) return
    const euler = new THREE.Euler(-mouse.current.y, mouse.current.x, 0)
    target.current.setFromEuler(euler)
    ref.current.quaternion.slerp(target.current, 0.05)
  })

  return ref
}

// ─── RenderTarget ─────────────────────────────────────────────────────────────
function useRenderTarget() {
  const renderTarget = useMemo(() => new THREE.WebGLCubeRenderTarget(256, {
    format: THREE.RGBAFormat,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
  }), [])
  const cubeCamera = useMemo(() => new THREE.CubeCamera(0.1, 1000, renderTarget), [renderTarget])
  const { gl, scene } = useThree()

  useFrame(() => {
    cubeCamera.update(gl, scene)
  })

  return [cubeCamera, renderTarget]
}

// ─── Colors ───────────────────────────────────────────────────────────────────
const BG_COLOR = '#921212'
const TITLE_COLOR = '#aaaaaa'
const CLICK_COLOR = '#f70131'
const REFLECT_COLOR = '#929292'
const DARK_COLOR = '#921212'

// ─── Components ───────────────────────────────────────────────────────────────
function Background() {
  return (
    <Octahedron args={[100]} position={[0, 0, -5]}>
      <meshBasicMaterial color={BG_COLOR} side={THREE.BackSide} />
    </Octahedron>
  )
}

function Title({ label, position, color = '#aaaaaa', scale, rotation }) {
  const ref = useRef()
  useEffect(() => {
    if (ref.current) ref.current.lookAt(0, 0, 0)
  }, [])
  return (
    <group ref={ref} position={position} scale={scale} rotation={rotation}>
      <Text
        depthTest={false}
        material-toneMapped={false}
        fontSize={4}
        font="https://fonts.gstatic.com/s/kanit/v7/nKKU-Go6G5tXcr4WPBWnVac.woff"
      >
        {label}
        <meshBasicMaterial color={color} />
      </Text>
    </group>
  )
}

function PhysicFloor(props) {
  usePlane(() => ({ ...props }))
  return (
    <Plane args={[1000, 1000]} {...props} receiveShadow>
      <shadowMaterial transparent opacity={0.2} />
    </Plane>
  )
}

function PhysicCube({ position, args, sideMat, reflectMat }) {
  const [ref, api] = useBox(() => ({
    mass: 1,
    position,
    args,
    material: { friction: 1, restitution: 0 },
  }))

  return (
    <Box
      ref={ref}
      args={args}
      onClick={() => api.applyImpulse([0, 0, -50], [0, 0, 0])}
      receiveShadow
      castShadow
      material={[sideMat, sideMat, sideMat, sideMat, reflectMat, sideMat]}
    />
  )
}

function Mirrors({ envMap }) {
  const ROWS = 4
  const COLS = 10
  const BOX_SIZE = 2

  const sideMat = useMemo(() => new THREE.MeshPhysicalMaterial({
    color: DARK_COLOR, envMap, roughness: 0.8, metalness: 0.2,
  }), [envMap])

  const reflectMat = useMemo(() => new THREE.MeshPhysicalMaterial({
    envMap, roughness: 0, metalness: 1, color: REFLECT_COLOR,
  }), [envMap])

  const cubesData = useMemo(() =>
    Array.from({ length: ROWS * COLS }, (_, index) => ({
      args: [BOX_SIZE, BOX_SIZE, BOX_SIZE],
      position: [
        -COLS + ((index * BOX_SIZE) % (BOX_SIZE * COLS)),
        -1 + BOX_SIZE * Math.floor((index * BOX_SIZE) / (BOX_SIZE * COLS)),
        0,
      ],
    })), [])

  return (
    <>
      {cubesData.map((cube, i) => (
        <PhysicCube
          key={i}
          {...cube}
          sideMat={sideMat}
          reflectMat={reflectMat}
        />
      ))}
    </>
  )
}

// ─── Scene ────────────────────────────────────────────────────────────────────
export default function Scene3() {
  const group = useSlerp()
  const [, renderTarget] = useRenderTarget()

  return (
    <>
      <group ref={group}>
        <Background />
        <Title label="Développeur WEB" position={[0, 4, -10]} color={TITLE_COLOR} />
        <Title label="Cliquez sur les cubes" position={[0, 2, 24]} color={CLICK_COLOR} scale={[-1, 1, 1]} />

        <Physics gravity={[0, -10, 0]}>
          <Mirrors envMap={renderTarget.texture} />
          <PhysicFloor rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} />
        </Physics>
      </group>

      <pointLight
        castShadow
        position={[0, 10, 2]}
        intensity={4}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={100}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />
    </>
  )
}
